import React from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "styled-components";
import { IListing } from "../../../common/commonTypes";
import NftCard from "../../../common/components/NftCard/NftCard";
import { routes } from "../../../routes";
import useMarket from "./useMarket";
import AlertMessage from "../../../common/components/AlertMessage";
import {
  getHeading3BoldTypography,
  getHeading4BoldTypography,
} from "../../../common/Typography";
import MarketFilters from "./MarketFilters";
import LoadingSpinner from "../../../common/components/LoadingSpinner";
import NoResults from "../../../common/components/NoResults/NoResults";
import InfinityListLoader from "../../../common/components/InfinityListLoader/InfinityListLoader";

const Root = styled.div``;

const Container = styled.div`
  padding-top: 128px;
  margin: 0 auto;
  padding-bottom: 137px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    padding-top: 40px;
    padding-bottom: 52px;
  }
`;

const AssetsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: auto auto;
  column-gap: 32px;
  row-gap: 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: auto;
  }
`;

const Title = styled.div`
  ${getHeading3BoldTypography};
  font-size: 40px;
  line-height: 48px;
  color: ${({ theme }) => theme.primaryColor};
  padding-bottom: 90px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    ${getHeading4BoldTypography};
    padding-bottom: 20px;
    line-height: 48px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 0 auto;
  padding-top: 45px;
`;

const Market = () => {
  const { t } = useTranslation();
  const {
    items,
    hasNextPage,
    category,
    lang,
    genre,
    sortBy,
    error,
    isLoading,
    sale,
    onFiltersUpdate,
    onResetFilters,
    onFetchNextPageMarket,
  } = useMarket();

  const playlist = items.map((item) => item.music);

  return (
    <Root>
      <Container className="main-container">
        <AlertMessage
          isOpen={!!error}
          message={error}
          style={{
            paddingBottom: "20px",
          }}
        />
        <Title>{t("market-title")}</Title>
        <MarketFilters
          category={category!}
          lang={lang!}
          genre={genre!}
          sortBy={sortBy!}
          isForSale={sale === "1"}
          onToggleForSale={(checked) => {
            onFiltersUpdate({ sale: checked ? "1" : "0" });
          }}
          onChangeCategory={(value) => {
            onFiltersUpdate({ category: value });
          }}
          onSelectLang={(option) => onFiltersUpdate({ lang: option?.value })}
          onSelectGenre={(option) => onFiltersUpdate({ genre: option?.value })}
          onSelectSortBy={(option) =>
            onFiltersUpdate({ sortBy: option?.value })
          }
          onResetFilters={onResetFilters}
        />

        <InfiniteScroll
          dataLength={items.length}
          loader={<InfinityListLoader />}
          next={onFetchNextPageMarket}
          hasMore={!!hasNextPage}
        >
          <AssetsContainer>
            {items.map((listing: IListing) => (
              <NftCard
                key={listing.id}
                music={listing.music}
                playlist={playlist}
                listing={listing}
                redirectUrl={routes.marketplace.id(
                  listing.music_id,
                  listing.id
                )}
              />
            ))}
          </AssetsContainer>
        </InfiniteScroll>

        {isLoading && (
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        )}

        {!isLoading && items.length === 0 && <NoResults />}
      </Container>
    </Root>
  );
};

export default Market;
